import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import { PrimaryAction, MaskPageBack } from '@/components/mask/MaskSystem';
import { dateLocale } from '@/lib/dateLocale';
import { toast } from 'sonner';
import '@/components/mask/mask.css';

/* Phase 4 — Tagebuch als ruhiger eigener Raum (GEDANKEN), inkl. Reflexionsnotizen zu Carma */

export default function TagebuchV2() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [carmaNotes, setCarmaNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  const formatDate = (iso) => new Date(iso).toLocaleDateString(dateLocale(i18n.language), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => {
    document.body.classList.add('cc-no-overlays');
    return () => document.body.classList.remove('cc-no-overlays');
  }, []);

  useEffect(() => {
    if (!user) { navigate('/auth'); return undefined; }
    let cancelled = false;
    Promise.all([
      axios.get(`${API_V2}/tagebuch`, { withCredentials: true }),
      axios.get(`${API_V2}/carma/room`, { withCredentials: true }).catch(() => ({ data: [] })),
    ])
      .then(([tb, room]) => {
        if (cancelled) return;
        setEntries(tb.data);
        setCarmaNotes(room.data.filter((m) => m.note));
      })
      .catch((err) => { if (!cancelled && err.response?.status === 401) navigate('/auth', { replace: true }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, API_V2, navigate]);

  const addEntry = async () => {
    const clean = text.trim();
    if (!clean || saving) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_V2}/tagebuch`, { text: clean }, { withCredentials: true });
      setEntries((prev) => [res.data, ...prev]);
      setText('');
    } catch (err) { toast.error(err.response?.data?.detail || t('v2.common.errorShort')); }
    finally { setSaving(false); }
  };

  const saveEdit = async (id) => {
    const clean = editDraft.trim();
    if (!clean) return;
    try {
      const res = await axios.put(`${API_V2}/tagebuch/${id}`, { text: clean }, { withCredentials: true });
      setEntries((prev) => prev.map((e) => (e.id === id ? res.data : e)));
      setEditingId(null);
    } catch (err) { toast.error(err.response?.data?.detail || t('v2.common.errorShort')); }
  };

  const removeEntry = async (id) => {
    try {
      await axios.delete(`${API_V2}/tagebuch/${id}`, { withCredentials: true });
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err) { toast.error(err.response?.data?.detail || t('v2.common.errorShort')); }
  };

  if (!user) return null;

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-x-hidden" data-testid="tagebuch-page">
      <div className="mask-bg-word mask-bg-top" aria-hidden="true">
        <span style={{ transform: 'translateX(-10%)' }}>{t('v2.bgWords.gedanken')}</span>
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-10 pb-28">
        <MaskPageBack onClick={() => navigate('/profile')} data-testid="tagebuch-back-btn" />

        <header className="mb-14">
          <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-5" data-testid="tagebuch-private-badge">{t('v2.tagebuch.private')}</p>
          <h1 className="mask-enter mask-enter-delay-1 mask-headline text-3xl sm:text-4xl text-[var(--text)]">
            {t('v2.ich.gedankenTitle')}
          </h1>
        </header>

        {/* Neuer Eintrag */}
        <div className="mb-20 mask-enter mask-enter-delay-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 5000))}
            placeholder={t('v2.tagebuch.placeholder')}
            data-testid="tagebuch-new-input"
            className="mask-textarea text-lg sm:text-xl text-[var(--text)] min-h-[18vh]"
          />
          <div className="mt-5">
            <PrimaryAction onClick={addEntry} disabled={saving || !text.trim()} data-testid="tagebuch-save-btn">
              {saving ? t('v2.common.loading') : t('v2.tagebuch.hold')}
            </PrimaryAction>
          </div>
        </div>

        {loading && <p className="text-sm text-[var(--muted)]">{t('v2.common.loading')}</p>}

        {/* Einträge */}
        {!loading && entries.length === 0 && carmaNotes.length === 0 && (
          <p className="text-base text-[var(--muted)]" data-testid="tagebuch-empty">
            {t('v2.tagebuch.empty')}
          </p>
        )}

        <div className="space-y-14">
          {entries.map((entry) => (
            <div key={entry.id} data-testid={`tagebuch-entry-${entry.id}`}>
              <p className="text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-3">{formatDate(entry.createdAt)}</p>
              {editingId === entry.id ? (
                <div>
                  <textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value.slice(0, 5000))}
                    data-testid={`tagebuch-edit-input-${entry.id}`}
                    className="mask-textarea text-lg text-[var(--text)] min-h-[14vh]"
                  />
                  <div className="mt-3 flex items-center gap-5">
                    <button type="button" onClick={() => saveEdit(entry.id)} data-testid={`tagebuch-update-${entry.id}`} className="text-sm text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 py-1">{t('v2.common.save')}</button>
                    <button type="button" onClick={() => setEditingId(null)} data-testid={`tagebuch-cancel-${entry.id}`} className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-1 transition-colors">{t('v2.common.notNow')}</button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="mask-letter text-lg sm:text-xl text-[var(--text)]/90">{entry.text}</p>
                  <div className="mt-3 flex items-center gap-5 text-sm text-[var(--muted)]">
                    <button type="button" onClick={() => { setEditingId(entry.id); setEditDraft(entry.text); }} data-testid={`tagebuch-edit-${entry.id}`} className="hover:text-[var(--text)] py-1 transition-colors">{t('v2.common.edit')}</button>
                    <button type="button" onClick={() => navigate('/give', { state: { prefillMessage: entry.text } })} data-testid={`tagebuch-carma-${entry.id}`} className="hover:text-[var(--text)] py-1 transition-colors">{t('v2.tagebuch.asCarma')}</button>
                    <button type="button" onClick={() => removeEntry(entry.id)} data-testid={`tagebuch-delete-${entry.id}`} className="hover:text-[var(--text)] py-1 transition-colors">{t('v2.common.delete')}</button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Reflexionsnotizen zu empfangenen Carma */}
        {carmaNotes.length > 0 && (
          <section className="mt-24" data-testid="tagebuch-carma-notes">
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-8">{t('v2.tagebuch.notesTitle')}</p>
            <div className="space-y-12">
              {carmaNotes.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => navigate('/inbox')}
                  data-testid={`tagebuch-carma-note-${m.id}`}
                  className="mask-room-item block w-full text-left"
                >
                  <span className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-2">
                    {t('v2.tagebuch.noteFrom', { name: m.senderName })}
                  </span>
                  <span className="block mask-letter text-lg text-[var(--text)]/85">{m.note}</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
